import { useState, useEffect } from "react";
import { Maturity, PromptPack, GameSettings, RoomMode } from "../../types";
import type { BasicSettings } from "../../components/menu/create-game/sections/BasicGameSettings";
import type { PromptSettings } from "../../components/menu/create-game/sections/PromptSettings";
import type { PlayerSettings } from "../../components/menu/create-game/sections/PlayerSettings";
import type { AdvancedSettings } from "../../components/menu/create-game/sections/AdvancedSettings";
import { config } from "../../config";

interface GameSettingsState {
  basic: BasicSettings;
  prompts: PromptSettings;
  players: PlayerSettings;
  advanced: AdvancedSettings;
}

interface UseGameSettingsReturn {
  settings: GameSettingsState;
  updateBasic: (updates: Partial<BasicSettings>) => void;
  updatePrompts: (updates: Partial<PromptSettings>) => void;
  updatePlayers: (updates: Partial<PlayerSettings>) => void;
  updateAdvanced: (updates: Partial<AdvancedSettings>) => void;
  resetToDefaults: () => void;
  getAllSettings: () => GameSettings;
}

interface PlayerDetails {
  meta: {
    mature: boolean;
  };
}


export interface ModeFlags {
  otp_mode?: boolean;
  self_insert_mode?: boolean;
  hidden_history?: boolean;
  solo_writing?: boolean;
}

/**
 * Convert ModeFlags to RoomMode for storage in RoomMeta
 */
export const getRoomModeFromFlags = (flags: ModeFlags): RoomMode => {
  if (flags.otp_mode) return RoomMode.Otp;
  if (flags.self_insert_mode) return RoomMode.SelfInsert;
  if (flags.hidden_history) return RoomMode.BrokenTelephone;
  if (flags.solo_writing) return RoomMode.Solo;
  return RoomMode.Standard;
};

/**
 * Determines the quick start guide setting.
 * In classic mode, always returns true. Otherwise returns the provided value or false.
 */
const getQuickStartGuide = (savedValue?: boolean): boolean => {
  return savedValue ?? false;
};

/**
 * Determines the write duration setting.
 * In classic mode, always returns 180 seconds (3 minutes). Otherwise returns the provided value or 180.
 */
const getWriteDuration = (savedValue?: number): number => {
  return savedValue ?? 180;
};

/**
 * Determines the moderation setting.
 * In classic mode, always returns false. Otherwise returns the provided value or false.
 */
const getModeration = (savedValue?: boolean): boolean => {
  return savedValue ?? false;
};

/**
 * Determines the prompt settings.
 * In classic mode, always returns forcedPrompts=true and multiPrompt=true.
 * Otherwise returns the provided values or defaults.
 */
const getPromptSettings = (
  savedValues?: Partial<PromptSettings>,
  modeFlags: ModeFlags = {}
): PromptSettings => {
  const defaults: PromptSettings = {
    forcedPrompts: false,
    multiPrompt: true,
    prompts: new Set([PromptPack.BASIC]),
  };


  // Solo mode forces prompts
  if (modeFlags.solo_writing && savedValues?.forcedPrompts !== undefined) {
    return {
      ...defaults,
      ...savedValues,
      forcedPrompts: true,
    };
  }

  return {
    ...defaults,
    ...savedValues,
  };
};

/**
 * Determines the advanced settings.
 * In classic mode, always returns fixed defaults with audiences disabled.
 * Otherwise returns the provided values or defaults.
 */
const getAdvancedSettings = (
  savedValues?: Partial<AdvancedSettings>
): AdvancedSettings => {
  const defaults: AdvancedSettings = {
    numSwitches: 3,
    selfShuffle: false,
    audiences: true,
    extraReadingTime: true,
    hideAuthors: false,
    allowDuplicateCharacters: false,
    autoSyncPartsToPlayers: false,
  };


  return {
    ...defaults,
    ...savedValues,
  };
};

const useGameSettings = (
  modeFlags: ModeFlags,
  playerDetails?: PlayerDetails
): UseGameSettingsReturn => {
  // Initialize state with defaults
  const [settings, setSettings] = useState<GameSettingsState>(() => ({
    basic: {
      quickStartGuide: getQuickStartGuide(),
      writeDuration: getWriteDuration(),
      maturity: Maturity.ALL_AGES,
      moderation: getModeration(),
    },
    prompts: getPromptSettings(undefined, modeFlags),
    players: {
      maxPlayers: 16,
      numCharactersSubmit: 5,
      numCharactersSelect: 2,
      characterMaturity: !!modeFlags.self_insert_mode,
    },
    advanced: getAdvancedSettings(),
  }));

  // Load settings from localStorage on mount
  useEffect(() => {
    const gameSettings = localStorage.getItem("gameSettings");
    if (gameSettings) {
      try {
        const parsed = JSON.parse(gameSettings);

        setSettings((prev) => ({
          basic: {
            ...prev.basic,
            ...(parsed.write_duration !== undefined && {
              writeDuration: getWriteDuration(parsed.write_duration),
            }),
            ...(parsed.show_instructions !== undefined && {
              quickStartGuide: getQuickStartGuide(parsed.show_instructions),
            }),
            ...(parsed.maturity !== undefined && {
              maturity:
                modeFlags.solo_writing
                  ? playerDetails?.meta.mature
                    ? Maturity.MATURE
                    : Maturity.ALL_AGES
                  : playerDetails?.meta.mature
                    ? parsed.maturity
                    : Maturity.ALL_AGES,
            }),
            ...(parsed.moderation !== undefined && {
              moderation: getModeration(parsed.moderation),
            }),
          },
          prompts: getPromptSettings({
            ...(parsed.forced_prompts !== undefined && {
              forcedPrompts: parsed.forced_prompts,
            }),
            ...(parsed.multi_prompt !== undefined && {
              multiPrompt: parsed.multi_prompt,
            }),
            prompts: prev.prompts.prompts,
          }, modeFlags),
          players: {
            ...prev.players,
            ...(parsed.max_players !== undefined && {
              maxPlayers: parsed.max_players,
            }),
            ...(parsed.num_characters_submit !== undefined && {
              numCharactersSubmit: parsed.num_characters_submit,
            }),
            ...(parsed.num_characters_select !== undefined && {
              numCharactersSelect: parsed.num_characters_select,
            }),
          },
          advanced: getAdvancedSettings({
            ...(parsed.hide_authors !== undefined && {
              hideAuthors: parsed.hide_authors,
            }),
            ...(parsed.self_shuffle !== undefined && {
              selfShuffle: parsed.self_shuffle,
            }),
            ...(parsed.num_switches !== undefined && {
              numSwitches: parsed.num_switches,
            }),
            ...(parsed.audiences !== undefined && {
              audiences: parsed.audiences,
            }),
            ...(parsed.no_extra_reading_time !== undefined && {
              extraReadingTime: !parsed.no_extra_reading_time,
            }),
            ...(parsed.allow_duplicate_characters !== undefined && {
              allowDuplicateCharacters: parsed.allow_duplicate_characters,
            }),
            ...(parsed.auto_sync_parts_to_players !== undefined && {
              autoSyncPartsToPlayers: parsed.auto_sync_parts_to_players,
            }),
          }),
        }));
      } catch (error) {
        console.warn("Failed to parse saved game settings:", error);
      }
    }

  }, [modeFlags, playerDetails?.meta.mature]);

  // Handle cross-setting dependencies
  useEffect(() => {
    if (settings.basic.maturity === Maturity.ALL_AGES) {
      // Disable character maturity and remove hardcore prompts for all-ages content
      setSettings((prev) => ({
        ...prev,
        players: {
          ...prev.players,
          characterMaturity: false,
        },
        prompts: {
          ...prev.prompts,
          prompts: new Set(
            [...prev.prompts.prompts].filter((p) => p !== PromptPack.HARDCORE)
          ),
        },
      }));
    }
  }, [settings.basic.maturity]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const gameSettingsForStorage = getAllSettings();
    localStorage.setItem(
      "gameSettings",
      JSON.stringify(gameSettingsForStorage)
    );
  }, [settings]);

  const updateBasic = (updates: Partial<BasicSettings>) => {
    setSettings((prev) => ({
      ...prev,
      basic: { ...prev.basic, ...updates },
    }));
  };

  const updatePrompts = (updates: Partial<PromptSettings>) => {
    setSettings((prev) => ({
      ...prev,
      prompts: { ...prev.prompts, ...updates },
    }));
  };

  const updatePlayers = (updates: Partial<PlayerSettings>) => {
    setSettings((prev) => ({
      ...prev,
      players: { ...prev.players, ...updates },
    }));
  };

  const updateAdvanced = (updates: Partial<AdvancedSettings>) => {
    setSettings((prev) => ({
      ...prev,
      advanced: { ...prev.advanced, ...updates },
    }));
  };

  const resetToDefaults = () => {
    setSettings({
      basic: {
        quickStartGuide: getQuickStartGuide(),
        writeDuration: getWriteDuration(),
        maturity: Maturity.ALL_AGES,
        moderation: getModeration(),
      },
      prompts: getPromptSettings(undefined, modeFlags),
      players: {
        maxPlayers: 16,
        numCharactersSubmit: 5,
        numCharactersSelect: 2,
        characterMaturity: !!modeFlags.self_insert_mode,
      },
      advanced: getAdvancedSettings(),
    });
  };

  const getAllSettings = (): GameSettings => {
    // Note: Mode-specific overrides (skip_voting, skip_shuffle, num_characters_submit, etc.)
    // are now handled by the backend's ApplyGameModeDefaults() based on roommeta.mode

    return {
      num_characters_submit: settings.players.numCharactersSubmit,
      num_characters_select: settings.players.numCharactersSelect,
      write_duration: settings.basic.writeDuration,
      show_instructions: settings.basic.quickStartGuide,
      maturity: settings.basic.maturity,
      hide_authors: settings.advanced.hideAuthors,
      max_players: settings.players.maxPlayers,
      moderation: settings.basic.moderation,
      num_switches: settings.advanced.numSwitches,
      self_shuffle: settings.advanced.selfShuffle,
      audiences: settings.advanced.audiences,
      multi_prompt: settings.prompts.multiPrompt,
      prompt_packs: Array.from(settings.prompts.prompts),
      no_extra_reading_time: !settings.advanced.extraReadingTime,
      forced_prompts:
        settings.prompts.forcedPrompts || !!modeFlags.solo_writing,
      character_maturity: settings.players.characterMaturity,
      allow_duplicate_characters: settings.advanced.allowDuplicateCharacters,
      auto_sync_parts_to_players: settings.advanced.autoSyncPartsToPlayers,
      // Spread mode flags - backend will apply mode-specific defaults
      ...modeFlags,
    };
  };

  return {
    settings,
    updateBasic,
    updatePrompts,
    updatePlayers,
    updateAdvanced,
    resetToDefaults,
    getAllSettings,
  };
};

export default useGameSettings;
;
